import noScroll from "@/utils/noScroll.js";

/**
 * Виджет всплывающего окна, привязанного к якорю.
 *
 * Окно открывается, когда в адресе появляется соответствующий якорь
 * (data-anchor → #anchor):
 *   - при внешнем переходе — страница открывается со ссылкой на якорь
 *     (проверяется при инициализации и по событию hashchange);
 *   - при переходе внутри страницы — клик по ссылке на этот якорь. Событие
 *     hashchange не срабатывает, если якорь в адресе уже стоит, поэтому клики
 *     по таким ссылкам перехватываются отдельно.
 *
 * Закрытие: крестик (data-popup-close), клик по фону окна или клавиша Esc.
 * При закрытии якорь убирается из адреса, чтобы повторный переход по той же
 * ссылке снова открыл окно.
 */
export default function anchorPopup(element, data) {
    const anchor = data.anchor;
    if (!anchor) {
        return;
    }

    const openClass = data.class || "open";
    const closeSelector = data.closeSelector || "[data-popup-close]";
    const hash = "#" + anchor;

    let isOpen = false;

    function open() {
        if (isOpen) {
            return;
        }
        isOpen = true;
        element.classList.add(openClass);
        noScroll.hideScroll();
    }

    function close() {
        if (!isOpen) {
            return;
        }
        isOpen = false;
        element.classList.remove(openClass);
        noScroll.showScroll();

        // Убираем якорь из адреса, не оставляя записи в истории и не
        // прокручивая страницу, чтобы по этой же ссылке окно открылось снова.
        if (window.location.hash === hash) {
            history.replaceState(null, "", window.location.pathname + window.location.search);
        }
    }

    function syncFromHash() {
        if (window.location.hash === hash) {
            open();
        }
    }

    // Закрытие по крестику и клику по фону (вне окна).
    element.addEventListener("click", function (event) {
        if (event.target === element || event.target.closest(closeSelector)) {
            close();
        }
    });

    // Закрытие по Esc.
    document.addEventListener("keydown", function (event) {
        if (event.key === "Escape" && isOpen) {
            close();
        }
    });

    // Успешная отправка формы внутри окна: форма уже очищена виджетом form,
    // прячем само окно — на экране остаётся только сообщение об успехе.
    element.addEventListener("form:success", close);

    // Переход внутри страницы: клик по ссылке на наш якорь. Реагируем только на
    // ссылки в пределах текущей страницы — переходы на другие страницы
    // выполняются как обычно, окно там откроется при загрузке.
    document.addEventListener("click", function (event) {
        const link = event.target.closest("a");
        if (!link) {
            return;
        }

        const url = new URL(link.href, window.location.href);
        if (url.hash !== hash || url.pathname !== window.location.pathname || url.host !== window.location.host) {
            return;
        }

        event.preventDefault();

        // Фиксируем якорь в адресе (без прокрутки), если его там ещё нет.
        if (window.location.hash !== hash) {
            history.pushState(null, "", hash);
        }

        open();
    });

    // Обычный/внешний переход по якорю уже открытой страницы.
    window.addEventListener("hashchange", syncFromHash);

    // Внешний переход: страница сразу открыта с нужным якорем.
    syncFromHash();
}
